<template>
  <nav
    id="app-header"
    :class="[
      'navbar',
      'is-fixed-top',
      { 'has-side-navigation': doesRouteHaveSideNavigation },
    ]"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="navbar-menu">
      <div class="navbar-start">
        <div v-if="doesRouteHaveSideNavigation" class="navbar-item menu-button__container">
          <b-icon icon="menu" @click.native="handleToggleSideNavigation(true)" />
        </div>
      </div>
      <div class="navbar-end">
        <div v-if="!isObjectEmpty(authUser) && isObjectEmpty(loggedInUser)" class="navbar-item">
          <div class="buttons">
            <b-button type="is-white" @click="handleLogOut" @keyup.enter="handleLogOut">Log Out</b-button>
          </div>
        </div>
        <div v-else-if="isObjectEmpty(authUser) && isObjectEmpty(loggedInUser)" class="navbar-item">
          <div class="buttons">
            <b-button type="is-white" tag="router-link" to="/log-in">Log In</b-button>
            <b-button type="is-primary" tag="router-link" to="/sign-up">Sign Up</b-button>
          </div>
        </div>
        <div v-else class="navbar-item">
          <div v-if="isLaunchStandupButtonVisible" class="u-m-r-small">
            <b-button
              v-if="loggedInUserTeamsArray.length < 2"
              @click.prevent="handleClickLaunchStandup(loggedInUserTeamsArray[0].id)"
              @keyup.enter="handleClickLaunchStandup(loggedInUserTeamsArray[0].id)"
              type="is-success"
              outlined
              size="is-small"
              :loading="isStandupLaunching"
            >
              Start Meeting
            </b-button>
            <b-dropdown position="is-bottom-left" :mobile-modal="false" v-else>
              <b-button
                slot="trigger"
                type="is-success"
                outlined
                size="is-small"
                :loading="isStandupLaunching"
              >
                Start Meeting
              </b-button>
              <b-dropdown-item
                v-for="team in loggedInUserTeamsArray"
                :key="team.id"
                @click="handleClickLaunchStandup(team.id)"
              >
                {{ team.teamName }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <b-dropdown class="u-m-b-xsmall" position="is-bottom-left" :mobile-modal="false">
            <div class="app-header__avatar__container" slot="trigger">
              <user-avatar
                :user="loggedInUser"
                :on-click="handleAvatarClick"
                size="small"
              />
            </div>
            <b-dropdown-item v-if="loggedInUser.workspaceId" @click="handleUserProfileClick">
              <b-icon icon="account" size="is-small" />&nbsp;&nbsp;My Profile
            </b-dropdown-item>
            <b-dropdown-item v-if="loggedInUser.workspaceId" separator />
            <b-dropdown-item @click="handleLogOut">
              <b-icon icon="logout" size="is-small" />&nbsp;&nbsp;Log Out
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import * as AuthActions from '@/store/actions/Auth.actions';
import * as UiMutations from '@/store/mutations/Ui.mutations';
import * as StandupActions from '@/store/actions/Standup.actions';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import UserAvatar from './UserAvatar.vue';

export default {
  name: 'AppHeader',

  components: {
    UserAvatar,
  },

  props: {
    doesRouteHaveSideNavigation: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isAvatarDropdownActive: false,
      isStandupLaunching: false,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
      standups: state => state.standup.standups,
      authUser: state => state.auth.authUser,
    }),
    ...mapGetters([
      'loggedInUser',
      'loggedInUserTeams',
    ]),
    loggedInUserTeamsArray() {
      return Object.values(this.loggedInUserTeams);
    },
    newStandupPayload() {
      return {
        alreadyReportedUserIds: [],
        // Drawn by the first person to open the standup, then shared with every viewer.
        reportingOrderUserIds: [],
        hasReportingStarted: false,
        isStandupReportingComplete: false,
        currentReportingUserId: null,
        // Whoever launches the standup drives it with the keyboard / presentation clicker.
        launchedByUserId: this.loggedInUser.id || null,
      };
    },
    isLaunchStandupButtonVisible() {
      return (
        !this.$route.path.includes('/onboarding')
        && !isObjectEmpty(this.loggedInUserTeams)
      );
    },
  },

  methods: {
    ...mapActions({
      logOutUser: AuthActions.LOG_OUT_USER,
      fetchCurrentDayStandupByTeam: StandupActions.FETCH_CURRENT_DAY_STANDUP_BY_TEAM,
      createStandup: StandupActions.CREATE_STANDUP,
    }),
    ...mapMutations({
      setSideNavigationState: UiMutations.SET_SIDE_NAVIGATION_STATE,
    }),
    isObjectEmpty,
    async handleLogOut() {
      await this.logOutUser();
    },
    handleToggleSideNavigation(isSideNavigationOpen) {
      this.setSideNavigationState({ isSideNavigationOpen });
    },
    handleAvatarClick(event) {
      event.preventDefault();
    },
    handleUserProfileClick() {
      this.$router.push('/my-profile');
    },
    async handleClickLaunchStandup(teamId) {
      this.isStandupLaunching = true;

      await this.fetchCurrentDayStandupByTeam({ teamId });

      const currentDayStandup = Object.values(this.standups).find(standup => standup.teamId === teamId);
      if (currentDayStandup) {
        this.$router.push(`/standup/${currentDayStandup.id}`);
      } else {
        const newStandupRef = await this.createStandup({ newStandup: { ...this.newStandupPayload, teamId } });

        this.$router.push(`/standup/${newStandupRef.id}`);
      }

      this.isStandupLaunching = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.has-side-navigation {
  right: 0;
  @media screen and (min-width: $breakpoint-to-hide-side-navigation) {
    width: calc(100vw - #{$side-navigation-width});
    left: $side-navigation-width;
  }
}

.navbar {
  box-shadow: $light-shadow;
}

.navbar__visitor-links {
  display: flex;
}

.menu-button__container {
  @media screen and (min-width: $breakpoint-to-hide-side-navigation) {
    display: none;
  }
}

.app-header__avatar__container {
  cursor: pointer;
}
</style>
